/**
 * <nia-variant-drawer>
 * Selector rápido de variante. Escucha nia:variant-picker:open, que emiten las
 * tarjetas de producto con más de una variante, y cancela el evento para que
 * la tarjeta no navegue a la ficha.
 *
 * Los datos salen de /products/{handle}.js, y el precio de suscripción se
 * calcula aplicando el price_adjustment real del selling plan, nunca un 15%
 * escrito a mano.
 */
const FOCUSABLE = 'a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])';

class NiaVariantDrawer extends HTMLElement {
  #product = null;
  #variant = null;
  #plan = null;
  #qty = 1;
  #lastFocus = null;

  connectedCallback() {
    this.modal = this.querySelector('.nia-vd__modal');
    this.moneyFormat = this.dataset.moneyFormat || '${{amount}}';

    this.addEventListener('click', this.#onClick);
    document.addEventListener('keydown', this.#onKeydown);
    document.addEventListener('nia:variant-picker:open', this.#onRequest);
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this.#onKeydown);
    document.removeEventListener('nia:variant-picker:open', this.#onRequest);
    document.body.style.overflow = '';
  }

  #onRequest = (event) => {
    const url = event.detail?.productUrl;
    if (!url) return;
    // Cancelar impide que la tarjeta navegue a la ficha del producto.
    event.preventDefault();
    this.#lastFocus = event.detail?.trigger ?? document.activeElement;
    this.#load(url);
  };

  async #load(productUrl) {
    try {
      const response = await fetch(`${productUrl.split('?')[0]}.js`, { headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error(`producto respondió ${response.status}`);

      this.#product = await response.json();
      this.#variant = this.#product.variants.find((v) => v.available) ?? this.#product.variants[0];
      this.#plan = null;
      this.#qty = 1;

      this.#paint();
      this.#open();
    } catch (error) {
      console.warn('[nia-variant-drawer] No se pudo cargar, navegando:', error);
      window.location.assign(productUrl);
    }
  }

  /** Formatea centavos con el money_format de la tienda. */
  #money(cents) {
    const amount = cents / 100;
    const withDecimals = amount.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    const noDecimals = Math.round(amount).toLocaleString('es-MX');

    return this.moneyFormat
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, noDecimals)
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, withDecimals)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, noDecimals)
      .replace(/\{\{\s*amount\s*\}\}/g, withDecimals);
  }

  /** Aplica el ajuste real del selling plan al precio de la variante. */
  #planPrice(variantPrice, plan) {
    const adjustment = plan?.price_adjustments?.[0];
    if (!adjustment) return variantPrice;

    switch (adjustment.value_type) {
      case 'percentage':
        return Math.round(variantPrice * (1 - adjustment.value / 100));
      case 'fixed_amount':
        return Math.max(0, variantPrice - adjustment.value);
      case 'price':
        return adjustment.value;
      default:
        return variantPrice;
    }
  }

  get #currentPrice() {
    const base = this.#variant?.price ?? 0;
    return this.#plan ? this.#planPrice(base, this.#plan) : base;
  }

  #paint() {
    const p = this.#product;
    if (!p) return;

    const thumb = this.querySelector('[data-nia-vd-thumb]');
    const image = this.#variant?.featured_image?.src ?? p.featured_image;
    if (thumb) {
      thumb.innerHTML = image ? `<img src="${image}" alt="" loading="lazy" decoding="async">` : '';
    }

    this.querySelector('[data-nia-vd-title]').textContent = p.title;

    // Variantes
    const group = this.querySelector('[data-nia-vd-variants]');
    const chips = this.querySelector('[data-nia-vd-chips]');
    const many = p.variants.length > 1;
    group.hidden = !many;
    if (many) {
      this.querySelector('[data-nia-vd-legend]').textContent = p.options?.[0] ?? 'Presentación';
      chips.innerHTML = p.variants
        .map(
          (v) =>
            `<button type="button" class="nia-vd__chip" data-nia-vd-variant="${v.id}"` +
            ` aria-pressed="${v.id === this.#variant.id}"${v.available ? '' : ' disabled'}>${v.title}</button>`
        )
        .join('');
    }

    // Planes de venta
    const plansGroup = this.querySelector('[data-nia-vd-plans]');
    const plans = p.selling_plan_groups?.[0]?.selling_plans ?? [];
    plansGroup.hidden = plans.length === 0;

    if (plans.length > 0) {
      const options = [{ id: '', name: 'Compra única' }, ...plans];
      this.querySelector('[data-nia-vd-plan-chips]').innerHTML = options
        .map((option) => {
          const active = String(this.#plan?.id ?? '') === String(option.id);
          let name = option.name;
          if (option.id) {
            const off = Math.round(100 - (this.#planPrice(this.#variant.price, option) / this.#variant.price) * 100);
            if (off > 0) name = `Suscripción −${off}%`;
          }
          return `<button type="button" class="nia-vd__plan" data-nia-vd-plan="${option.id}" aria-pressed="${active}">${name}</button>`;
        })
        .join('');

      this.querySelector('[data-nia-vd-plan-note]').textContent = this.#plan
        ? 'Cada 30 días. Cancela cuando quieras.'
        : '';
    }

    this.#paintPrice();
  }

  #paintPrice() {
    const unit = this.#currentPrice;
    this.querySelector('[data-nia-vd-price]').textContent =
      this.#money(unit) + (this.#plan ? ' /mes' : '');
    this.querySelector('[data-nia-vd-count]').textContent = String(this.#qty);
    this.querySelector('[data-nia-vd-submit]').textContent = `Agregar — ${this.#money(unit * this.#qty)}`;

    const minus = this.querySelector('[data-nia-vd-qty="-1"]');
    if (minus) minus.disabled = this.#qty <= 1;
  }

  #open() {
    this.classList.add('is-open');
    this.modal?.removeAttribute('inert');
    document.body.style.overflow = 'hidden';
    // Igual que en el carrito lateral: al enfocar, el panel aún está fuera
    // de cuadro y sin la bandera el navegador desplazaría la ventana.
    this.modal?.querySelector(FOCUSABLE)?.focus({ preventScroll: true });
  }

  #close = () => {
    if (!this.classList.contains('is-open')) return;
    this.classList.remove('is-open');
    this.modal?.setAttribute('inert', '');
    document.body.style.overflow = '';
    this.#setError('');
    if (this.#lastFocus instanceof HTMLElement) this.#lastFocus.focus();
  };

  #setError(message) {
    const box = this.querySelector('[data-nia-vd-error]');
    if (!box) return;
    box.textContent = message;
    box.hidden = message === '';
  }

  #onClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    // Cierra la × o un clic en el fondo. El modal está dentro del overlay, así
    // que hay que comparar contra el elemento exacto y no con closest().
    if (target.closest('.nia-vd__close') || target.classList.contains('nia-vd__overlay')) {
      event.preventDefault();
      this.#close();
      return;
    }

    const variantBtn = target.closest('[data-nia-vd-variant]');
    if (variantBtn) {
      const id = Number(variantBtn.dataset.niaVdVariant);
      this.#variant = this.#product.variants.find((v) => v.id === id) ?? this.#variant;
      this.#paint();
      return;
    }

    const planBtn = target.closest('[data-nia-vd-plan]');
    if (planBtn) {
      const id = planBtn.dataset.niaVdPlan;
      const plans = this.#product.selling_plan_groups?.[0]?.selling_plans ?? [];
      this.#plan = id ? plans.find((p) => String(p.id) === id) ?? null : null;
      this.#paint();
      return;
    }

    const stepBtn = target.closest('[data-nia-vd-qty]');
    if (stepBtn instanceof HTMLButtonElement && !stepBtn.disabled) {
      this.#qty = Math.max(1, this.#qty + Number(stepBtn.dataset.niaVdQty));
      this.#paintPrice();
      return;
    }

    if (target.closest('[data-nia-vd-submit]')) this.#add();
  };

  #onKeydown = (event) => {
    if (!this.classList.contains('is-open')) return;

    if (event.key === 'Escape') {
      event.preventDefault();
      this.#close();
      return;
    }

    if (event.key !== 'Tab' || !this.modal) return;

    const focusable = Array.from(this.modal.querySelectorAll(FOCUSABLE)).filter((el) => !el.hidden);
    if (focusable.length === 0) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  async #add() {
    const submit = this.querySelector('[data-nia-vd-submit]');
    if (!this.#variant || !submit) return;

    submit.disabled = true;
    this.#setError('');

    const item = { id: this.#variant.id, quantity: this.#qty };
    if (this.#plan) item.selling_plan = this.#plan.id;

    try {
      const response = await fetch(`${window.Shopify?.routes?.root ?? '/'}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [item] }),
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.description || `cart/add.js respondió ${response.status}`);
      }

      this.#close();
      document.getElementById('nia-cart-drawer')?.openWithRefresh?.();
    } catch (error) {
      this.#setError(error.message || 'No se pudo agregar. Inténtalo de nuevo.');
    } finally {
      submit.disabled = false;
    }
  }
}

if (!customElements.get('nia-variant-drawer')) {
  customElements.define('nia-variant-drawer', NiaVariantDrawer);
}
